/**
 * Tidal-stream SOURCE selector. The app can show one of several real, distinct
 * source datasets at a time, so students can flick between them on the same
 * date/time and see how the sources differ in rate, direction and coverage.
 *
 * Sources exposed:
 *  - 'chart': our 22 transcribed chart diamonds (springs/neaps hourly tables,
 *    exact charted positions), interpolated by hour-relative-to-HW Victoria and
 *    the day's spring/neap state. Works across the whole training year.
 *  - 'enav': the RYA eNavigation dataset, the same diamonds plus 20 extra
 *    "secondary" coverage points. eNav is itself a springs/neaps + HW-Victoria
 *    model, so rather than storing every day of the year (~4 MB of half-hourly
 *    data) we captured a springs date and a neaps date, reduced each point to a
 *    springs/neaps hourly table referred to HW Victoria, and evaluate it for any
 *    date the same way the chart diamonds are. This reproduces eNav's published
 *    values for the whole year (it agrees with the chart diamonds to about a
 *    quarter of a knot) in a few kilobytes, with no single-date fallback. The
 *    secondary points are georeferenced (~1 km approx); the diamonds sit on the
 *    exact charted positions.
 */
import { TIDAL_DIAMONDS, streamAt, tidalStateAt } from './index';
import type { DiamondHour, TidalDiamond } from './index';
import enavData from './enav-streams.json';

export type StreamSourceId = 'chart' | 'enav';

export interface StreamSourceMeta {
  id: StreamSourceId;
  name: string;
  character: string;
}

/** The selectable sources, in display order. */
export const STREAM_SOURCES: StreamSourceMeta[] = [
  { id: 'chart', name: 'Chart diamonds', character: '22 diamonds · exact positions' },
  { id: 'enav', name: 'RYA eNav', character: '+ 20 coverage points (~1 km)' },
];

export interface StreamArrow {
  /** [lon, lat]. */
  position: [number, number];
  /** Set, degrees true. */
  setDeg: number;
  /** Rate, knots. */
  rateKn: number;
  /** Exact charted diamond, or approximate (~1 km) secondary coverage point. */
  kind: 'diamond' | 'secondary';
  /** Short label, e.g. "3A", "4D", "S7". */
  label: string;
  /** True when the position is georeferenced/approximate. */
  approx: boolean;
}

interface EnavPoint {
  name: string;
  kind: 'diamond' | 'secondary';
  position: [number, number];
  approx: boolean;
  /** Springs/neaps hourly table, −6…+6 relative to HW Victoria. */
  hours: DiamondHour[];
}
interface EnavDoc {
  refPort: string;
  mhwsM: number;
  mhwnM: number;
  derivedFrom: string[];
  note: string;
  points: EnavPoint[];
}
const ENAV = enavData as EnavDoc;

/** The eNav sample dates the springs/neaps model was derived from. */
export const ENAV_DERIVED_FROM: string[] = ENAV.derivedFrom;

/** Short label from a diamond chart+letter or an eNav point name. */
function chartLabel(chart: string, letter: string): string {
  return `${chart === 'c3' ? '3' : '4'}${letter}`;
}
function enavLabel(name: string): string {
  if (name === 'RYA3456D') return 'D';
  if (name.startsWith('RYA35')) return `3${name.slice(5)}`;
  if (name.startsWith('RYA46')) return `4${name.slice(5)}`;
  if (name.startsWith('RYATS')) return `S${name.slice(5)}`;
  return name;
}

/** Arrows for the active source at an instant. Only the chosen source renders. */
export function streamArrowsAt(source: StreamSourceId, ms: number): StreamArrow[] {
  const { hoursFromHW, springRatio } = tidalStateAt(ms);
  if (source === 'enav') {
    return ENAV.points.map((p) => {
      const s = streamAt({ hours: p.hours } as TidalDiamond, hoursFromHW, springRatio);
      return {
        position: p.position,
        setDeg: s.setDeg,
        rateKn: s.rateKn,
        kind: p.kind,
        label: enavLabel(p.name),
        approx: p.approx,
      };
    });
  }
  // chart diamonds: springs/neaps interpolated by HW-Victoria state
  return TIDAL_DIAMONDS.map((d) => {
    const s = streamAt(d, hoursFromHW, springRatio);
    return {
      position: d.position,
      setDeg: s.setDeg,
      rateKn: s.rateKn,
      kind: 'diamond' as const,
      label: chartLabel(d.chart, d.letter),
      approx: false,
    };
  });
}
